import { LoadingButton } from "@mui/lab";
import { Button, Dialog, Slide } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { ReactElement, ReactNode, createElement, forwardRef, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons/lib";
import { TbAlertTriangle, TbInfoCircle } from "react-icons/tb";

const Transition = forwardRef(function Transition(
    props: TransitionProps & {
        children: ReactElement;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

type ModalOptions = {
    title?: string;
    description: string;
    onClose: () => void;
    open: boolean;
    color: "error" | "primary";
    loading: boolean;
    handleConfirm: () => void;
    html?: boolean;
    descriptionOpts?: Record<string, string | number>;
    children?: ReactNode;
    icon?: IconType;
    confirmText?: string;
    cancelText?: string;
    /** After open transition, focus confirm (Enter then activates default button behavior). */
    focusConfirmOnOpen?: boolean;
};

const colors = {
    error: {
        icon: <TbAlertTriangle size={30} className="!text-red-600" />,
        title: "confirmations.areYouSure",
        cancelBtn: "buttons.cancel",
        confirmBtn: "buttons.confirm",
        iconBg: "bg-red-100",
        confirmColor: "error",
        iconClass: "text-red-600",
    },
    primary: {
        icon: <TbInfoCircle size={30} className="!text-blue-600" />,
        iconBg: "bg-blue-100",
        title: "confirmations.areYouSure",
        cancelBtn: "buttons.cancel",
        confirmBtn: "buttons.confirm",
        confirmColor: "primary",
        iconClass: "text-blue-600",
    },
};
const CommonAlert = ({
    open,
    onClose,
    color,
    title,
    loading,
    description,
    handleConfirm,
    html,
    descriptionOpts,
    children,
    icon,
    confirmText,
    cancelText,
    focusConfirmOnOpen = false,
}: ModalOptions) => {
    const item = useMemo(() => colors[color], [color]);
    const { t } = useTranslation();
    const confirmButtonRef = useRef<HTMLButtonElement>(null);
    return (
        <Dialog
            TransitionComponent={Transition}
            TransitionProps={{
                onEntered: () => {
                    if (focusConfirmOnOpen) confirmButtonRef.current?.focus({ preventScroll: true });
                },
            }}
            open={open}
            onClose={onClose}
            maxWidth="sm"
        >
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                    <div
                        className={
                            `mx-auto flex w-14 h-14  animate-pulse  shrink-0  items-center justify-center rounded-full ` +
                            item.iconBg
                        }
                    >
                        {icon ? createElement(icon, { fontSize: 30, className: item.iconClass }) : item.icon}
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <div className="text-base font-semibold text-gray-900">{t(title || item.title)}</div>
                        <div className="mt-2">
                            <div className="text-sm text-gray-500">
                                {html ? (
                                    <span dangerouslySetInnerHTML={{ __html: t(description, descriptionOpts) }}></span>
                                ) : (
                                    t(description, descriptionOpts)
                                )}
                            </div>
                        </div>
                        {children || ""}
                    </div>
                </div>
            </div>
            <div className="bg-gray-50 px-4 py-3  flex justify-end gap-3">
                <Button disabled={loading} onClick={() => onClose()} variant="outlined" color="secondary">
                    {t(cancelText || item.cancelBtn)}
                </Button>
                <LoadingButton
                    ref={confirmButtonRef}
                    onClick={handleConfirm}
                    loading={loading}
                    variant="contained"
                    color={
                        item.confirmColor as
                            | "error"
                            | "primary"
                            | "inherit"
                            | "secondary"
                            | "success"
                            | "info"
                            | "warning"
                    }
                >
                    {t(confirmText || item.confirmBtn)}
                </LoadingButton>
            </div>
        </Dialog>
    );
};

export default CommonAlert;
